import { ArrowLeft, Check, CheckCheck, Mic, MicOff, Repeat, Shield, Users, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { ChipSelect, Field, FitBadge, HexAvatar, Loading, ShareButton, StatusChip } from '../components';
import { formatDateTime, formatDuration, formatRelative, toDatetimeLocalValue } from '../format';
import { Link } from '../router';
import { useApp } from '../store';
import { COMMEND_TAGS, type Participant, type SessionDetailPayload } from '../types';

function ReviewRow({ sessionId, participant, done, onDone }: { sessionId: string; participant: Participant; done: boolean; onDone: (points: number) => void }) {
  const [choice, setChoice] = useState<boolean | null | undefined>(undefined);
  const [tags, setTags] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(done);
  const { toast } = useApp();

  async function submit(wouldPlayAgain: boolean | null) {
    setChoice(wouldPlayAgain);
    setBusy(true);
    try {
      const result = await api.submitReview(sessionId, participant.userId, wouldPlayAgain, tags);
      setSaved(true);
      onDone(result.pointsEarned);
      if (result.pointsEarned > 0) toast(`Review saved — +${result.pointsEarned} points`, 'success');
      else toast('Review saved.', 'success');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save the review.', 'error');
    } finally {
      setBusy(false);
    }
  }

  if (saved) {
    return (
      <div className="review-row done">
        <span className="host-line"><HexAvatar label={participant.avatar} size="sm" icon={participant.avatarIcon} accent={participant.avatarColor} />{participant.username}</span>
        <span className="pill mint"><CheckCheck size={13} /> Reviewed</span>
      </div>
    );
  }

  return (
    <div className="review-row">
      <div className="review-row-head">
        <span className="host-line"><HexAvatar label={participant.avatar} size="sm" icon={participant.avatarIcon} accent={participant.avatarColor} />{participant.username}</span>
        <span className="review-question">Play together again?</span>
      </div>
      <ChipSelect
        options={COMMEND_TAGS.map((tag) => ({ value: tag, label: tag }))}
        selected={tags}
        onToggle={(tag) => setTags((current) => current.includes(tag) ? current.filter((item) => item !== tag) : current.length < 3 ? [...current, tag] : current)}
      />
      <div className="review-actions">
        <button className="secondary small" disabled={busy} onClick={() => submit(true)} aria-label={`Would play with ${participant.username} again: yes`}><Check size={14} /> Yes</button>
        <button className="ghost small" disabled={busy} onClick={() => submit(null)} aria-label={`Would play with ${participant.username} again: not sure`}>Not sure</button>
        <button className="ghost small" disabled={busy} onClick={() => submit(false)} aria-label={`Would play with ${participant.username} again: no`}><X size={14} /> No</button>
      </div>
      {choice !== undefined && busy && <Loading label="Saving…" />}
    </div>
  );
}

function EditSessionForm({ session, busy, onSave }: { session: SessionDetailPayload['session']; busy: boolean; onSave: (fields: Record<string, unknown>) => void }) {
  const [title, setTitle] = useState(session.title);
  const [description, setDescription] = useState(session.description);
  const [startValue, setStartValue] = useState(toDatetimeLocalValue(session.startTime));
  const [duration, setDuration] = useState(session.durationMinutes);
  const [partySize, setPartySize] = useState(session.partySize);

  return (
    <form
      className="stack-form edit-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({
          title,
          description,
          startTime: new Date(startValue).toISOString(),
          durationMinutes: duration,
          partySize
        });
      }}
    >
      <Field label="Title">
        <input value={title} onChange={(event) => setTitle(event.target.value)} minLength={4} maxLength={80} required />
      </Field>
      <Field label="Description">
        <textarea value={description} onChange={(event) => setDescription(event.target.value)} maxLength={500} rows={2} />
      </Field>
      <div className="field-row">
        <Field label="Start time">
          <input type="datetime-local" value={startValue} onChange={(event) => setStartValue(event.target.value)} required />
        </Field>
        <Field label="Duration (minutes)">
          <input type="number" min={15} max={360} step={15} value={duration} onChange={(event) => setDuration(Number(event.target.value))} />
        </Field>
        <Field label="Party size">
          <input type="number" min={session.currentPlayers} max={12} value={partySize} onChange={(event) => setPartySize(Number(event.target.value))} />
        </Field>
      </div>
      <button className="primary small" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
    </form>
  );
}

export function SessionDetail({ id }: { id: string }) {
  const { profile, authStatus, toast } = useApp();
  const [data, setData] = useState<SessionDetailPayload | null>(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [role, setRole] = useState('');
  const [editing, setEditing] = useState(false);
  const justCreated = new URLSearchParams(window.location.search).get('created') === '1';

  const load = useCallback(() => {
    api.session(id)
      .then(setData)
      .catch(() => setMissing(true));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (missing) {
    return (
      <div className="page narrow">
        <h1>Session not found</h1>
        <p>It may have been canceled or the link is wrong.</p>
        <Link to="/sessions" className="secondary small">Back to sessions</Link>
      </div>
    );
  }
  if (!data) return <Loading label="Loading session…" />;

  const { session, participants, waitlist, viewer, review } = data;
  const iAmIn = Boolean(viewer?.joined);
  const iAmWaitlisted = Boolean(viewer?.waitlisted);
  const isHost = Boolean(viewer?.isHost);
  const me = participants.find((participant) => participant.userId === viewer?.id);
  const upcoming = session.status === 'open' || session.status === 'full' || session.status === 'in_progress';
  const canJoin = !iAmIn && !iAmWaitlisted && upcoming && session.status !== 'completed';
  const willWaitlist = session.openSpots <= 0;
  const reviewOpen = session.status === 'completed' && new Date() < new Date(review.closesAt) && iAmIn;
  const teammatesToReview = reviewOpen ? participants.filter((participant) => participant.userId !== viewer?.id) : [];

  async function act(action: () => Promise<unknown>, message?: string) {
    setBusy(true);
    try {
      await action();
      if (message) toast(message, 'success');
      load();
      return true;
    } catch (error) {
      toast(error instanceof Error ? error.message : 'That did not work. Try again.', 'error');
      return false;
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page narrow">
      <div className="detail-topline">
        <Link to="/sessions" className="back-link"><ArrowLeft size={15} /> All sessions</Link>
        <ShareButton url={`/sessions/${session.id}`} title={session.title} text={`Join my ${session.gameName} session on TeamCraft`} />
      </div>

      {justCreated && (
        <div className="share-callout">
          <div>
            <strong>Session created 🎉</strong>
            <small>Share it so your squad can join in one tap.</small>
          </div>
          <ShareButton url={`/sessions/${session.id}`} title={session.title} text={`Join my ${session.gameName} session on TeamCraft`} label="Share session" variant="primary small" />
        </div>
      )}

      <div className="detail-head">
        <span className="game-tag" style={session.gameAccent ? { color: session.gameAccent } : undefined}>
          {session.gameName} · {session.activity}
        </span>
        <StatusChip status={session.status} />
      </div>
      <h1>{session.title}</h1>
      {(session.guild || session.repeatsWeekly) && (
        <div className="card-badges">
          {session.guild && <Link to={`/guilds/${session.guild.slug}`} className="pill mint"><Shield size={11} /> {session.guild.name}</Link>}
          {session.repeatsWeekly && <span className="pill"><Repeat size={11} /> Repeats weekly</span>}
        </div>
      )}
      {session.description && <p className="detail-description">{session.description}</p>}

      <div className="detail-meta">
        <span className="mono">{formatDateTime(session.startTime)} ({formatRelative(session.startTime)})</span>
        <span className="mono">{formatDuration(session.durationMinutes)}</span>
        <span><Users size={14} /> {session.currentPlayers}/{session.partySize} joined</span>
        <span>{session.voice === 'none' ? <MicOff size={14} /> : <Mic size={14} />} {session.voice === 'required' ? 'Voice required' : session.voice === 'none' ? 'No voice' : 'Voice optional'}</span>
        <span>{session.platform}</span>
        <span>{session.language}</span>
      </div>

      {session.requiredRoles.length > 0 && (
        <div className="roles" aria-label="Roles wanted">{session.requiredRoles.map((item) => <span key={item}>{item}</span>)}</div>
      )}

      {profile && <FitBadge session={session} profile={profile} />}

      <section className="roster" aria-label="Roster">
        <div className="section-heading slim">
          <h2>Roster</h2>
          {(session.status === 'open' || session.status === 'full') && iAmIn && (
            <span className="mono muted">{session.readyCount}/{session.currentPlayers} ready</span>
          )}
        </div>
        {participants.map((participant) => (
          <div key={participant.userId} className="roster-row">
            <Link to={`/players/${participant.username}`} className="host-line">
              <HexAvatar label={participant.avatar} size="sm" icon={participant.avatarIcon} accent={participant.avatarColor} />{participant.username}
            </Link>
            <span className="roster-tags">
              {participant.isHost && <span className="pill">Host</span>}
              {participant.role && !participant.isHost && <span className="pill">{participant.role}</span>}
              {participant.ready && <span className="pill mint"><Check size={12} /> Ready</span>}
              {isHost && !participant.isHost && (session.status === 'open' || session.status === 'full' || session.status === 'in_progress') && (
                <button
                  className="ghost small"
                  disabled={busy}
                  onClick={() => { if (window.confirm(`Remove ${participant.username} from this session?`)) void act(() => api.kickPlayer(session.id, participant.userId), `${participant.username} removed.`); }}
                >
                  Remove
                </button>
              )}
            </span>
          </div>
        ))}
        {session.openSpots > 0 && session.status !== 'completed' && session.status !== 'canceled' && (
          <div className="roster-row open-slot">{session.openSpots} open {session.openSpots === 1 ? 'spot' : 'spots'}</div>
        )}
      </section>

      {waitlist.length > 0 && (
        <section className="roster" aria-label="Waitlist">
          <div className="section-heading slim"><h2>Waitlist</h2><span className="mono muted">{waitlist.length} waiting</span></div>
          {waitlist.map((entry, index) => (
            <div key={entry.userId} className="roster-row">
              <Link to={`/players/${entry.username}`} className="host-line">
                <span className="waitlist-position mono">{index + 1}</span>
                <HexAvatar label={entry.avatar} size="sm" />{entry.username}
              </Link>
            </div>
          ))}
        </section>
      )}

      {authStatus === 'guest' && canJoin && (
        <div className="action-bar">
          <Link to="/signup" className="primary">Create an account to join</Link>
        </div>
      )}

      {authStatus === 'authed' && (
        <div className="action-bar">
          {canJoin && (
            <>
              {session.requiredRoles.length > 0 && (
                <ChipSelect
                  options={session.requiredRoles.map((item) => ({ value: item, label: item }))}
                  selected={role ? [role] : []}
                  onToggle={(value) => setRole(value === role ? '' : value)}
                  single
                />
              )}
              <button className="primary" disabled={busy} onClick={() => act(() => api.joinSession(session.id, role || null), willWaitlist ? 'Added to the waitlist — we’ll ping you if a spot opens.' : 'You are in. See you at start time.')}>
                {willWaitlist ? 'Join waitlist' : 'Join session'}
              </button>
            </>
          )}
          {iAmWaitlisted && (
            <>
              <span className="pill">On the waitlist</span>
              <button className="ghost" disabled={busy} onClick={() => act(() => api.leaveSession(session.id), 'Left the waitlist.')}>
                Leave waitlist
              </button>
            </>
          )}
          {iAmIn && (session.status === 'open' || session.status === 'full') && (
            <button className="secondary" disabled={busy} onClick={() => act(() => api.toggleReady(session.id))}>
              {me?.ready ? 'Unready' : 'Ready up'}
            </button>
          )}
          {iAmIn && !isHost && (session.status === 'open' || session.status === 'full') && (
            <button className="ghost" disabled={busy} onClick={() => act(() => api.leaveSession(session.id), 'You left the session.')}>
              Leave
            </button>
          )}
          {isHost && (session.status === 'open' || session.status === 'full') && (
            <>
              <button className="secondary" disabled={busy} onClick={() => setEditing((current) => !current)}>
                {editing ? 'Close editor' : 'Edit details'}
              </button>
              <button className="ghost danger" disabled={busy} onClick={() => { if (window.confirm('Cancel this session for everyone?')) void act(() => api.manageSession(session.id, 'cancel'), 'Session canceled.'); }}>
                Cancel session
              </button>
            </>
          )}
          {isHost && session.status === 'in_progress' && (
            <button className="secondary" disabled={busy} onClick={() => act(() => api.manageSession(session.id, 'complete'), 'Marked complete — time for reviews.')}>
              Mark complete
            </button>
          )}
        </div>
      )}

      {editing && isHost && (session.status === 'open' || session.status === 'full') && (
        <EditSessionForm
          session={session}
          busy={busy}
          onSave={(fields) => act(() => api.editSession(session.id, fields), 'Session updated.').then((saved) => { if (saved) setEditing(false); })}
        />
      )}

      {reviewOpen && teammatesToReview.length > 0 && (
        <section className="review-panel" aria-label="Review teammates">
          <div className="section-heading slim">
            <div>
              <span className="eyebrow">After game</span>
              <h2>Would you play together again?</h2>
            </div>
            <span className="mono muted">closes {formatRelative(review.closesAt)}</span>
          </div>
          <p>Mutual reviews confirm the session and earn both players points. Add up to three commend tags.</p>
          {teammatesToReview.map((participant) => (
            <ReviewRow
              key={participant.userId}
              sessionId={session.id}
              participant={participant}
              done={review.reviewedUserIds.includes(participant.userId)}
              onDone={() => load()}
            />
          ))}
        </section>
      )}

      {session.status === 'completed' && !iAmIn && (
        <p className="muted">This session has ended.</p>
      )}
    </div>
  );
}
